import type Konva from 'konva';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { Circle, Rect } from 'react-konva';
import { toast } from 'sonner';
import {
  type BrushStroke,
  extendBrushStroke,
  finalizeBrushStroke,
  startBrushStroke,
} from '~/features/background/brushStroke';
import { findRegionAt, floodFromSeed } from '~/features/background/keyRegion';
import { pickBgColorAt } from '~/features/background/pick';
import { useStore } from '~/store';
import { CROP_COLOR, WM_COLOR } from './colors';

type Draft = { x: number; y: number; w: number; h: number };

const LEAK_ASPECT_THRESHOLD = 8;
const LEAK_FILL_RATIO = 0.05;

export function DrawModeOverlay({ stageRef }: { stageRef: RefObject<Konva.Stage | null> }) {
  const drawMode = useStore((s) => s.editor.drawMode);
  const setDrawMode = useStore((s) => s.setDrawMode);
  const setCropRect = useStore((s) => s.setCropRect);
  const setWatermarkRegion = useStore((s) => s.setWatermarkRegion);
  const setWatermarkEnabled = useStore((s) => s.setWatermarkEnabled);
  const setBgColors = useStore((s) => s.setBgColors);
  const addRegion = useStore((s) => s.addRegion);
  const removeRegion = useStore((s) => s.removeRegion);
  const setSelectedRegionId = useStore((s) => s.setSelectedRegionId);
  const subtractStroke = useStore((s) => s.subtractStroke);
  const clearOffsets = useStore((s) => s.clearDividers);

  const brushRadius = useStore((s) => s.background.brushRadius);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [eraseCursor, setEraseCursor] = useState<{ x: number; y: number } | null>(null);
  const [eraseSubtract, setEraseSubtract] = useState(false);
  const strokeRef = useRef<BrushStroke | null>(null);
  const strokeSubtractRef = useRef(false);

  useEffect(() => {
    if (drawMode !== 'erase') return;
    const sync = (e: KeyboardEvent) => setEraseSubtract(e.shiftKey);
    window.addEventListener('keydown', sync);
    window.addEventListener('keyup', sync);
    return () => {
      window.removeEventListener('keydown', sync);
      window.removeEventListener('keyup', sync);
      setEraseSubtract(false);
    };
  }, [drawMode]);

  useEffect(() => {
    if (drawMode === 'none') {
      setDraft(null);
      return;
    }
    const stage = stageRef.current;
    if (!stage) return;

    if (drawMode === 'keyRegion') {
      const onClick = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
        const pos = stage.getRelativePointerPosition();
        const { source, background } = useStore.getState();
        if (!pos || !source.imageData) return;
        const seedX = Math.round(pos.x);
        const seedY = Math.round(pos.y);

        const hit = findRegionAt(background.regions, seedX, seedY);
        const shift = 'shiftKey' in e.evt && e.evt.shiftKey;
        if (hit) {
          if (shift) removeRegion(hit.id);
          else setSelectedRegionId(hit.id);
          return;
        }

        const cleaned = background.cleaned;
        const { width: iw } = source.imageData;
        // Bail early: seed pixel is already fully keyed out in the cleaned image.
        if (cleaned && (cleaned.data[(seedY * iw + seedX) * 4 + 3] ?? 255) === 0) return;

        const tol = background.wandTolerance;
        const region = floodFromSeed(
          source.imageData,
          background.bgColors,
          { x: seedX, y: seedY },
          tol,
          background.hardEdge,
          cleaned,
        );
        if (region) {
          const { w: bw, h: bh } = region.bbox;
          const aspect = Math.max(bw / bh, bh / bw);
          if (aspect > LEAK_ASPECT_THRESHOLD && region.pixelCount / (bw * bh) < LEAK_FILL_RATIO) {
            toast.warning('Wand leaked — try lowering tolerance or use the eraser.');
            return;
          }
          addRegion(region);
          setSelectedRegionId(region.id);
        }
      };
      stage.on('mousedown.draw touchstart.draw', onClick);
      return () => {
        stage.off('mousedown.draw touchstart.draw');
      };
    }

    if (drawMode === 'erase') {
      const commitStroke = () => {
        const stroke = strokeRef.current;
        const subtract = strokeSubtractRef.current;
        strokeRef.current = null;
        strokeSubtractRef.current = false;
        if (!stroke) return;
        const dims = useStore.getState().source.dimensions;
        if (!dims) return;
        if (subtract) {
          subtractStroke(stroke);
          return;
        }
        const region = finalizeBrushStroke(stroke, dims);
        if (region.pixelCount > 0) {
          addRegion(region);
          setSelectedRegionId(region.id);
        }
      };

      const moveCursor = (pos: { x: number; y: number }) => {
        setEraseCursor((prev) =>
          prev && prev.x === pos.x && prev.y === pos.y ? prev : { x: pos.x, y: pos.y },
        );
      };

      const onDown = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
        const pos = stage.getRelativePointerPosition();
        if (!pos) return;
        const { background, source } = useStore.getState();
        const dims = source.dimensions;
        if (!dims) return;
        strokeSubtractRef.current = 'shiftKey' in e.evt && e.evt.shiftKey;
        strokeRef.current = startBrushStroke(
          pos,
          background.brushRadius,
          background.brushSoftness,
          dims.w,
        );
        extendBrushStroke(strokeRef.current, pos, dims);
        moveCursor(pos);
      };

      const onMove = () => {
        const pos = stage.getRelativePointerPosition();
        if (!pos) return;
        moveCursor(pos);
        if (!strokeRef.current) return;
        const dims = useStore.getState().source.dimensions;
        if (!dims) return;
        extendBrushStroke(strokeRef.current, pos, dims);
      };

      const onLeave = () => {
        setEraseCursor(null);
        commitStroke();
      };

      stage.on('mousedown.draw touchstart.draw', onDown);
      stage.on('mousemove.draw touchmove.draw', onMove);
      stage.on('mouseup.draw touchend.draw', commitStroke);
      stage.on('mouseleave.draw', onLeave);
      return () => {
        stage.off('mousedown.draw touchstart.draw');
        stage.off('mousemove.draw touchmove.draw');
        stage.off('mouseup.draw touchend.draw');
        stage.off('mouseleave.draw');
        setEraseCursor(null);
      };
    }

    if (drawMode === 'pickBg') {
      const onPick = () => {
        const pos = stage.getRelativePointerPosition();
        const { source, background } = useStore.getState();
        if (pos && source.imageData) {
          const next = pickBgColorAt(
            source.imageData,
            Math.round(pos.x),
            Math.round(pos.y),
            background.bgColors,
          );
          if (next) setBgColors(next);
        }
      };
      stage.on('mousedown.draw touchstart.draw', onPick);
      return () => {
        stage.off('mousedown.draw touchstart.draw');
      };
    }

    if (drawMode !== 'crop' && drawMode !== 'watermark') return;

    let start: { x: number; y: number } | null = null;

    const onDown = () => {
      const pos = stage.getRelativePointerPosition();
      if (!pos) return;
      // In crop mode, don't start a new draft when the pointer is inside the
      // existing rect — let the Rect/Transformer handle the drag instead.
      if (drawMode === 'crop') {
        const cropRect = useStore.getState().grid.cropRect;
        if (
          cropRect &&
          pos.x >= cropRect.x &&
          pos.x <= cropRect.x + cropRect.w &&
          pos.y >= cropRect.y &&
          pos.y <= cropRect.y + cropRect.h
        )
          return;
      }
      start = pos;
      setDraft({ x: pos.x, y: pos.y, w: 0, h: 0 });
    };

    const onMove = () => {
      if (!start) return;
      const pos = stage.getRelativePointerPosition();
      if (!pos) return;
      setDraft({
        x: Math.min(start.x, pos.x),
        y: Math.min(start.y, pos.y),
        w: Math.abs(pos.x - start.x),
        h: Math.abs(pos.y - start.y),
      });
    };

    const onUp = () => {
      if (!start) return;
      // Read final pointer position; fall back to start on touchend where pointer may be gone.
      const pos = stage.getRelativePointerPosition() ?? start;
      const rx = Math.max(0, Math.round(Math.min(start.x, pos.x)));
      const ry = Math.max(0, Math.round(Math.min(start.y, pos.y)));
      let rw = Math.round(Math.abs(pos.x - start.x));
      let rh = Math.round(Math.abs(pos.y - start.y));
      const mode = drawMode;
      start = null;
      setDraft(null);
      setDrawMode('none');
      if (rw < 4 || rh < 4) return;
      // Read dims at commit time — avoids re-wiring handlers on every image resize.
      const dims = useStore.getState().source.dimensions;
      if (dims) {
        rw = Math.min(rw, dims.w - rx);
        rh = Math.min(rh, dims.h - ry);
      }
      if (mode === 'crop') {
        setCropRect({ x: rx, y: ry, w: rw, h: rh });
        clearOffsets();
      } else if (mode === 'watermark') {
        setWatermarkRegion({ x: rx, y: ry, w: rw, h: rh });
        setWatermarkEnabled(true);
      }
    };

    stage.on('mousedown.draw touchstart.draw', onDown);
    stage.on('mousemove.draw touchmove.draw', onMove);
    stage.on('mouseup.draw touchend.draw', onUp);
    return () => {
      stage.off('mousedown.draw touchstart.draw');
      stage.off('mousemove.draw touchmove.draw');
      stage.off('mouseup.draw touchend.draw');
    };
  }, [
    drawMode,
    stageRef,
    setDrawMode,
    setCropRect,
    setWatermarkRegion,
    setWatermarkEnabled,
    setBgColors,
    addRegion,
    removeRegion,
    setSelectedRegionId,
    subtractStroke,
    clearOffsets,
  ]);

  if (drawMode === 'erase') {
    if (!eraseCursor) return null;
    const inner = eraseSubtract ? '#34d399' : 'white';
    return (
      <>
        <Circle
          x={eraseCursor.x}
          y={eraseCursor.y}
          radius={brushRadius}
          stroke="black"
          strokeWidth={2}
          strokeScaleEnabled={false}
          listening={false}
        />
        <Circle
          x={eraseCursor.x}
          y={eraseCursor.y}
          radius={brushRadius}
          stroke={inner}
          strokeWidth={1}
          strokeScaleEnabled={false}
          listening={false}
        />
      </>
    );
  }

  if (drawMode === 'none' || !draft) return null;
  const stroke = drawMode === 'crop' ? CROP_COLOR : WM_COLOR;
  return (
    <Rect
      x={draft.x}
      y={draft.y}
      width={draft.w}
      height={draft.h}
      stroke={stroke}
      strokeWidth={2}
      strokeScaleEnabled={false}
      listening={false}
      fill={`${stroke}1A`}
    />
  );
}
